import Grid from '@mui/material/Grid';
import { useSearchParams } from 'react-router-dom';
import { useMemo, useState, Dispatch, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AnyAction } from 'redux';
import { differenceInCalendarDays, isValid } from 'date-fns';
import { Stores, AppPermissions, conciliationHeaderKeys } from '../types';
import { dateResponseMap } from '../helpers';

import {
  findConciliationDetailRequested,
  reportConciliationDetailLoadingSelector,
  changeConciliationHeaderId,
  changeConciliationDetailFilterStore,
  changeClosingFilterConciliationDetailErrors,
  changeConciliationDetailFilterStartAccountingDate,
  changeConciliationDetailFilterEndAccountingDate,
  reportFilterCountryCodeSelector,
  reportConciliationDetailFilterStoresSelector,
  reportConciliationDetailFilterStartDateSelector,
  reportConciliationDetailFilterEndDateSelector,
  reportDetailLimitDaysSelector,
  // findConciliationHeaderRequested,
  findPosRequested,
} from '../state';

import {
  CountryFilter,
  ReportFilterActions,
  ReportFilters,
  StoreFilter,
  ErrorFilter,
  AccountingDateRangeFilter,
  PosFilter,
} from './ReportFilters';

import { PermissionsProvider } from './Permissions';
import DateRangeLimitModal from './Modals/DateRangeLimitModal';

const dateFilterConfig = {
  selectorStartDate: reportConciliationDetailFilterStartDateSelector,
  selectorEndDate: reportConciliationDetailFilterEndDateSelector,
  actionStartDate: changeConciliationDetailFilterStartAccountingDate,
  actionEndDate: changeConciliationDetailFilterEndAccountingDate,
};

const onedriveAction = () => findConciliationDetailRequested(true);

const handleSearchParams = (dispatch: Dispatch<AnyAction>, searchParams: URLSearchParams) => {
  const countryCode = searchParams.get(conciliationHeaderKeys.country);
  const store = searchParams.get(conciliationHeaderKeys.store);
  const conciliationHeaderId = searchParams.get(conciliationHeaderKeys.conciliationHeaderId);
  const accountingDate = dateResponseMap(
    searchParams.get(conciliationHeaderKeys.accountingDate)
  ) as Date;

  if (accountingDate) {
    dispatch(dateFilterConfig.actionStartDate(accountingDate));
    dispatch(dateFilterConfig.actionEndDate(accountingDate));
  }

  if (store && countryCode) {
    const stores = [
      {
        countryCode,
        storeAcronym: store,
      },
    ] as Stores[];
    dispatch(changeConciliationDetailFilterStore(stores));
  }

  if (conciliationHeaderId) {
    dispatch(changeConciliationHeaderId(conciliationHeaderId));
    dispatch(changeClosingFilterConciliationDetailErrors([]));
    dispatch(findConciliationDetailRequested());
  }
};

const ConciliationDetailSearch = () => {
  const countryCode = useSelector(reportFilterCountryCodeSelector);
  const startDate = useSelector(dateFilterConfig.selectorStartDate);
  const endDate = useSelector(dateFilterConfig.selectorEndDate);
  const detailLimitDays = useSelector(reportDetailLimitDaysSelector);
  const selectedStores = useSelector(reportConciliationDetailFilterStoresSelector);

  const [searchParams] = useSearchParams();
  const stores = useSelector(reportConciliationDetailFilterStoresSelector);
  const dispatch = useDispatch();

  useEffect(() => {
    handleSearchParams(dispatch, searchParams);
  }, [dispatch, searchParams]);

  const storesIds = useMemo(
    () =>
      stores
        ?.map(s => s.storeAcronym)
        .sort()
        .join(',') || '',
    [stores]
  );

  useEffect(() => {
    const hasStore = storesIds.length > 0;
    const hasDates = startDate.accept && endDate.accept;

    if (countryCode && hasStore && hasDates) {
      dispatch(findPosRequested());
    }
  }, [dispatch, countryCode, storesIds, startDate.accept, endDate.accept]);

  const applyAction = () => {
    dispatch(changeConciliationHeaderId(''));
    return findConciliationDetailRequested();
  };
  const isTooLarge = useMemo(() => {
    if (selectedStores?.length) return false;
    const start = startDate?.accept;
    const end = endDate?.accept;
    if (!start || !end) return false;
    if (!isValid(start) || !isValid(end)) return false;
    const days = differenceInCalendarDays(end, start) + 1;
    return days > detailLimitDays;
  }, [startDate, endDate, detailLimitDays, selectedStores]);

  const applyDisabled = !(startDate.accept && endDate.accept && countryCode);

  const [modalOpen, setModalOpen] = useState(false);

  const beforeApply = () => {
    if (!isTooLarge) return true;
    setModalOpen(true);
    return false;
  };

  return (
    <ReportFilters
      loadingSelector={reportConciliationDetailLoadingSelector}
      applyAction={applyAction}
      onedriveAction={onedriveAction}
      showOpStructureFilters
    >
      <PermissionsProvider resourcePermissions={[AppPermissions.CONCILIATIONS_DETAILS]}>
        <Grid container spacing={3} alignItems="center">
          <Grid item xs={12} md={2} lg={2} xl={2}>
            <CountryFilter fullWidth />
          </Grid>
          <Grid item xs={12} md={3} lg={2} xl={2}>
            <StoreFilter
              selector={reportConciliationDetailFilterStoresSelector}
              action={changeConciliationDetailFilterStore}
            />
          </Grid>
          <Grid item xs={12} md lg xl>
            <AccountingDateRangeFilter config={dateFilterConfig} spacing={1} />
          </Grid>
          <Grid item xs={12} md={4} lg={2} xl={2} sx={{ minWidth: '12em' }}>
            <ErrorFilter />
          </Grid>
          <Grid item xs={12} md={4} lg={2} xl={2} sx={{ minWidth: '12em' }}>
            <PosFilter />
          </Grid>
          <Grid item xs={12} md lg xl>
            <DateRangeLimitModal
              open={modalOpen}
              onClose={() => setModalOpen(false)}
              maxDays={detailLimitDays}
            />
            <ReportFilterActions applyDisabled={applyDisabled} beforeApply={beforeApply} />
          </Grid>
        </Grid>
      </PermissionsProvider>
    </ReportFilters>
  );
};

export default ConciliationDetailSearch;
